/**
 * sentry.client.config.ts — runs in the browser, in every React island.
 *
 * Activates only when PUBLIC_SENTRY_DSN is set at build time (Cloudflare
 * Pages env vars for bbb-website, Fly env vars for bbb-app). Without
 * the DSN this whole module is a no-op so dev / preview / CI builds
 * never ship instrumentation.
 */
import * as Sentry from "@sentry/astro";

const dsn = import.meta.env.PUBLIC_SENTRY_DSN || "";

if (dsn) {
  Sentry.init({
    dsn,
    environment: (import.meta.env.PUBLIC_SENTRY_ENV as string) || "production",
    release:     (import.meta.env.PUBLIC_SENTRY_RELEASE as string) || undefined,

    // Performance sampling — keep low; we mainly want errors.
    tracesSampleRate: 0.05,

    // Browser-side replay sampling — useful for reproducing UI bugs.
    // 10% of sessions where ANY error occurs (gets the context). Set the
    // base session rate to 0 so we're not paying replay storage for
    // healthy sessions.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.10,

    integrations: [
      Sentry.replayIntegration({
        // Mask all input + media by default — privacy-first. Even though
        // we don't put tenant data into form inputs (most chat lives in
        // an SSE stream), be conservative until we audit.
        maskAllInputs: true,
        maskAllText:   false,    // text is fine; mask only inputs
        blockAllMedia: true,
      }),
    ],

    // Don't send default PII (user IP, browser fingerprint, etc.) by
    // default. Override per-event if we ever need it for a specific
    // debugging session.
    sendDefaultPii: false,

    // Per Sentry's pricing model: errors are free up to the org quota;
    // performance traces consume the same quota. The low rate above is
    // the right starting point.
  });
}
